/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { logger } from "matrix-js-sdk/lib/logger";
import { BehaviorSubject } from "rxjs";

import { PosthogAnalytics } from "../analytics/PosthogAnalytics";
import { type Behavior } from "../state/Behavior";
import { useBehavior } from "../useBehavior";
import { type BackgroundBlurLevel } from "../livekit/backgroundBlur";

/**
 * Значение, прочитанное из хранилища. Обёртка нужна, чтобы отличить «для этой
 * комнаты значения нет» от «для этой комнаты сохранено undefined»: у настроек
 * устройств undefined — это законное значение (устройство не выбрано).
 */
interface StoredValue<T> {
  value: T;
}

function readStoredValue<T>(key: string): StoredValue<T> | undefined {
  const storedValue = localStorage.getItem(key);
  if (storedValue === null) return undefined;
  try {
    return { value: JSON.parse(storedValue) as T };
  } catch (e) {
    logger.warn(`Invalid value stored for setting ${key}: ${storedValue}.`, e);
    return undefined;
  }
}

/**
 * Настройка, у которой бывает своё значение на комнату. Держим список, чтобы
 * при переходе в другую комнату перечитать их все одним махом.
 */
interface RoomScopedSetting {
  reloadRoomValue: () => void;
}

const roomScopedSettings = new Set<RoomScopedSetting>();

/** Комната, к настройкам которой сейчас привязано приложение. */
let currentRoomId: string | undefined;

/**
 * Привязывает настройки к комнате: с этого момента камера, микрофон, динамик и
 * размытие читаются и сохраняются отдельно для неё.
 *
 * Беда была такая: настройки были общими на всё приложение, и человеку,
 * который на планёрке сидит с камерой и размытием, а на разговоре с подрядчиком
 * — без камеры, приходилось переставлять их вручную при каждом входе. Теперь
 * правка внутри звонка остаётся в этой комнате, а общее значение продолжает
 * работать как значение по умолчанию для всех остальных.
 *
 * `undefined` — вернуться к общим настройкам (мы вне комнаты).
 */
export function setSettingsRoom(roomId: string | undefined): void {
  if (currentRoomId === roomId) return;
  currentRoomId = roomId;
  for (const setting of roomScopedSettings) setting.reloadRoomValue();
}

/** Комната, к которой сейчас привязаны настройки. */
export function getSettingsRoom(): string | undefined {
  return currentRoomId;
}

interface SettingOptions {
  /**
   * Настройку можно переопределить для отдельной комнаты. Общее значение при
   * этом остаётся значением по умолчанию для комнат без своего.
   */
  perRoom?: boolean;
}

export class Setting<T> {
  public constructor(
    key: string,
    public readonly defaultValue: T,
    { perRoom = false }: SettingOptions = {},
  ) {
    this.key = `matrix-setting-${key}`;
    this.perRoom = perRoom;

    this.global = readStoredValue<T>(this.key) ?? { value: defaultValue };
    this.roomOverride = this.readRoomOverride();

    this._value$ = new BehaviorSubject(
      (this.roomOverride ?? this.global).value,
    );
    this.value$ = this._value$;

    if (perRoom) roomScopedSettings.add(this);
  }

  private readonly key: string;
  private readonly perRoom: boolean;

  /** Общее значение — оно же значение по умолчанию для новых комнат. */
  private global: StoredValue<T>;
  /** Значение текущей комнаты, если она его переопределила. */
  private roomOverride: StoredValue<T> | undefined;

  private readonly _value$: BehaviorSubject<T>;
  public readonly value$: Behavior<T>;

  private roomKey(roomId: string): string {
    return `${this.key}__room-${roomId}`;
  }

  private readRoomOverride(): StoredValue<T> | undefined {
    if (!this.perRoom || currentRoomId === undefined) return undefined;
    return readStoredValue<T>(this.roomKey(currentRoomId));
  }

  private publish(): void {
    const value = (this.roomOverride ?? this.global).value;
    // Молчим, если ничего не изменилось: иначе смена комнаты дёргала бы
    // пересоздание видеодорожки на пустом месте.
    if (!Object.is(value, this._value$.getValue())) this._value$.next(value);
  }

  /** Перечитать значение после перехода в другую комнату. */
  public readonly reloadRoomValue = (): void => {
    this.roomOverride = this.readRoomOverride();
    this.publish();
  };

  public readonly setValue = (value: T): void => {
    if (this.perRoom && currentRoomId !== undefined) {
      // Внутри комнаты правка остаётся в ней и не трогает общее значение,
      // иначе выбор в одном звонке молча переезжал бы во все остальные.
      this.roomOverride = { value };
      localStorage.setItem(this.roomKey(currentRoomId), JSON.stringify(value));
    } else {
      this.global = { value };
      localStorage.setItem(this.key, JSON.stringify(value));
    }
    this.publish();
  };
  public readonly getValue = (): T => {
    return this._value$.getValue();
  };
}

/**
 * React hook that returns a settings's current value and a setter.
 */
export function useSetting<T>(setting: Setting<T>): [T, (value: T) => void] {
  return [useBehavior(setting.value$), setting.setValue];
}

// null = undecided
export const optInAnalytics = new Setting<boolean | null>(
  "opt-in-analytics",
  null,
);
// TODO: This setting can be disabled. Work out an approach to disableable
// settings thats works for Observables in addition to React.
export const useOptInAnalytics = (): [
  boolean | null,
  ((value: boolean | null) => void) | null,
] => {
  const setting = useSetting(optInAnalytics);
  return PosthogAnalytics.instance.isEnabled() ? setting : [false, null];
};

export const developerMode = new Setting("developer-settings-tab", false);

export const duplicateTiles = new Setting("duplicate-tiles", 0);

export const showNonMemberTiles = new Setting<boolean>(
  "show-non-member-tiles",
  false,
);
export const debugTileLayout = new Setting("debug-tile-layout", false);

export const showConnectionStats = new Setting<boolean>(
  "show-connection-stats",
  false,
);

export const audioInput = new Setting<string | undefined>(
  "audio-input",
  undefined,
  { perRoom: true },
);
export const audioOutput = new Setting<string | undefined>(
  "audio-output",
  undefined,
  { perRoom: true },
);
export const videoInput = new Setting<string | undefined>(
  "video-input",
  undefined,
  { perRoom: true },
);

/**
 * Раньше размытие было выключателем, и в хранилище у людей лежит true/false по
 * ключу background-blur. Заведи мы ступени с чистого листа — у всех, кто уже
 * включил размытие, оно бы после обновления молча пропало, и человек узнал бы
 * об этом от собеседника. Поэтому прежнее «включено» читаем как среднюю
 * ступень.
 */
function initialBlurLevel(): BackgroundBlurLevel {
  return readStoredValue<boolean>("matrix-setting-background-blur")?.value ===
    true
    ? "medium"
    : "off";
}

export const backgroundBlur = new Setting<BackgroundBlurLevel>(
  "background-blur-level",
  initialBlurLevel(),
  { perRoom: true },
);

/**
 * Картинка за человеком вместо размытия.
 *
 * Пусто — картинки нет; имя встроенной («light», «dark», «teal») или «custom» —
 * своя, лежащая в IndexedDB. Саму картинку сюда класть нельзя: настройки живут
 * в localStorage, а там несколько мегабайт на всё.
 *
 * Отдельной настройкой, а не ступенью размытия: выбор один на двоих, и в окне
 * настроек это один список — выбрал картинку, размытие выключилось само.
 */
export const backgroundImage = new Setting<string>("background-image", "", {
  perRoom: true,
});

export const showReactions = new Setting<boolean>("reactions-show", true);

export const playReactionsSound = new Setting<boolean>(
  "reactions-play-sound",
  true,
);

export const soundEffectVolume = new Setting<number>(
  "sound-effect-volume",
  0.5,
);

export const useNewMembershipManager = new Setting<boolean>(
  "new-membership-manager",
  true,
);

export const useExperimentalToDeviceTransport = new Setting<boolean>(
  "experimental-to-device-transport",
  true,
);

export const muteAllAudio = new Setting<boolean>("mute-all-audio", false);

export const alwaysShowSelf = new Setting<boolean>("always-show-self", true);

export const joinWithMicrophoneMuted = new Setting<boolean>(
  "join-with-microphone-muted",
  true,
);

/**
 * Как вести медиапоток звонка: автоматически, только по TCP или только по UDP.
 *
 * Медиа и так идёт через ретранслятор (см. connectLiveKitOnce), но сам ретранслятор
 * доступен двумя путями: UDP на порту 3479 и TLS поверх TCP на 5350. Согласование
 * выбирает UDP, если он «работает». Беда в том, что через туннель UDP работает
 * наполовину: замер с машины CTO 31.08 — 30% потерь и лучший пакет 786 мс, тогда как
 * TCP до того же сервера отвечал за 1–4 мс. Половинная работа хуже полного отказа:
 * при отказе согласование ушло бы на запасной путь само, а при потерях оно остаётся.
 *
 * Отсюда выбор руками. «Автоматически» — прежнее поведение, ничего не трогаем.
 */
export type CallTransportMode = "auto" | "tcp" | "udp";

export const callTransportMode = new Setting<CallTransportMode>(
  "call-transport-mode",
  "auto",
);

/**
 * Форма кадра собеседника: строгий прямоугольник или экран кинескопа с выгнутыми
 * сторонами. Решение CTO 09.09.2026 — выбор отдали человеку, а не зашили.
 * Демонстрация экрана остаётся прямоугольной при любом выборе: на ней читают
 * текст и таблицы.
 */
export type TileShape = "rectangle" | "crt" | "tv";

export const tileShape = new Setting<TileShape>("tile-shape", "crt");

export const joinWithCameraMuted = new Setting<boolean>(
  "join-with-camera-muted",
  true,
);

export const alwaysShowIphoneEarpiece = new Setting<boolean>(
  "always-show-iphone-earpiece",
  false,
);

export const televisionIntensity = new Setting<number>(
  "television-intensity",
  0.2,
);
